/**
 * Centralized English copy for runidea.ai.
 *
 * Phase 5 will swap this map for a real i18n library (e.g. react-i18next)
 * by replacing every `strings.foo.bar` with `t('foo.bar')`. Until then,
 * keeping strings here makes that swap trivial — and prevents copy from
 * leaking into JSX where translation tooling can't see it.
 */
export const strings = {
  common: {
    new_deal_cta: "New Deal Analysis",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    open: "Open",
    back: "Back",
    next: "Next",
    loading: "Loading…",
    error_generic: "Something went wrong. Please try again.",
    coming_soon_billing: "Billing coming soon. Email support@runidea.ai for early access.",
  },
  brand: {
    name: "runidea.ai",
    tagline: "AI-powered real estate investing.",
    mascot_tagline: "Let the monkey do his thing.",
    legal_footer: "Generated by runidea.ai — for informational purposes only. Not investment advice.",
  },
  portfolio: {
    title: "Portfolio Intelligence",
    subtitle: "A live view of your entire real estate book.",
    empty_title: "No properties in your portfolio yet",
    empty_body: "Add your first deal to start building your real estate book.",
    empty_cta: "Analyze your first deal",
    kpis: {
      properties: "Total properties",
      market_value: "Total market value",
      monthly_cash_flow: "Total monthly cash flow",
      avg_cap_rate: "Avg cap rate",
    },
    sections: {
      cashflow_perf: "Cash flow performance",
      cashflow_perf_sub: "Monthly cash flow per deal",
      verdict_dist: "Verdict distribution",
      verdict_dist_sub: "How your book breaks down",
      risk_concentration: "Risk concentration",
      by_state: "By state",
      by_type: "By property type",
      highlights: "Performance highlights",
      best_performer: "Best performer",
      worst_performer: "Worst performer",
      all_deals: "All deals",
    },
    filters: {
      all: "All",
      strong_buy: "Strong Buy",
      buy: "Buy",
      hold: "Hold",
      risky: "Risky",
      reject: "Reject",
    },
  },
  reports: {
    title: "Reports",
    subtitle: "Generate lender- and partner-grade investment reports.",
    select_deal: "Select a deal",
    download_pdf: "Download PDF",
    open_deal: "Open deal",
    rerun_ai: "Re-run AI analysis",
    info: "PDF includes property, financials, risk, AI recommendation, and offer strategy.",
    empty_title: "Analyze a deal to generate your first report",
    empty_body: "Reports pull everything from your deal — property facts, financials, AI verdict, risk, and offer strategy — into one shareable PDF.",
    empty_cta: "Start your first analysis",
    preview: {
      title: "Investment Analysis Report",
      prepared_for: "Prepared for",
      generated: "Generated",
      property_summary: "Property summary",
      financial_summary: "Financial summary",
      financing_assumptions: "Financing assumptions",
      risk_analysis: "Risk analysis",
      ai_recommendation: "AI recommendation",
      offer_strategy: "Offer strategy",
      overall_risk: "Overall risk",
      top_risks: "Top risks",
      top_opportunities: "Top opportunities",
      broker_questions: "Broker questions",
      seller_motivation_clues: "Seller motivation clues",
      realtor_email: "Realtor email template",
      estimated_label: "Estimated — no live data source",
    },
  },
  settings: {
    title: "Account & workspace",
    plans_title: "Plans",
    plans_subtitle: "Pricing UI preview — full checkout ships in a future release.",
    plans: {
      free: {
        name: "Free",
        price: "$0",
        period: "/mo",
        cta_current: "Current plan",
        features: [
          "5 deals analyzed per month",
          "AI Analyst, Risk, Negotiation (basic)",
          "1 user",
          "Community support",
        ],
      },
      pro: {
        name: "Pro",
        price: "$29",
        period: "/mo",
        recommended: "Recommended",
        cta: "Upgrade to Pro",
        features: [
          "Unlimited deals",
          "AI Analyst, Risk, Negotiation (full)",
          "Portfolio Intelligence",
          "PDF Reports",
          "AI Copilot Chat",
          "1 user",
          "Email support",
        ],
      },
      enterprise: {
        name: "Enterprise",
        price: "Custom",
        period: "",
        cta: "Contact sales",
        features: [
          "Everything in Pro",
          "Multi-user team (coming)",
          "SSO + audit log (coming)",
          "API access (coming)",
          "Dedicated support",
        ],
      },
    },
  },
  copilot: {
    title: "runidea Copilot",
    empty_title: "Ask anything. Link to a deal for context.",
    empty_body: "I cite real numbers from your deals — cap rate, cash flow, DSCR — and never make up data.",
    new_conversation: "New conversation",
    placeholder: "Ask anything about real estate investing…",
    linked_label: "Linked",
    open_deal: "Open",
    standalone: "No deal (standalone)",
    quick_prompts: [
      "Would you buy this deal?",
      "What should I offer?",
      "What are the top risks?",
      "How can I improve this deal?",
    ],
    delete_confirm_title: "Delete this conversation?",
    delete_confirm_body: "This permanently removes the conversation.",
    error_load: "Failed to load Copilot.",
  },
  ai: {
    fallback_badge: "AI service unavailable — showing baseline analysis",
    agree_engine: "AI agrees with the engine",
    disagree_engine: "AI disagrees — see analysis",
    hero_empty_title: "Get an investor-grade verdict in 5 seconds.",
    hero_empty_body:
      "Claude Sonnet 4.5 reads every input + computed metric on this deal and produces a full risk / opportunity / negotiation breakdown.",
    run_analysis: "Run AI Analysis",
    regenerate: "Regenerate",
    analyzing: "Analyzing…",
    regenerating: "Regenerating…",
    suggested_offer: "Suggested offer",
    deal_score: "Deal score",
  },
};

export default strings;
